import { useEffect, useState } from "react";
import { Mail, CheckCircle2, Loader2, Plug, Unplug, Eye, EyeOff, Send, AlertTriangle } from "lucide-react";
import { api, fmtError } from "../lib/api";
import { toast } from "sonner";

export const AdminEmail = () => {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ host: "smtp.ionos.fr", port: 587, username: "contact@laconsoledesartistes.fr", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);

  const load = () =>
    api.get("/admin/smtp/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));

  useEffect(() => {
    load();
  }, []);

  const connect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/smtp/connect", form);
      toast.success(`Email de test envoyé depuis ${form.username} — vérifiez votre boîte !`);
      setForm((f) => ({ ...f, password: "" }));
      load();
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTestBusy(true);
    try {
      await api.post("/admin/smtp/test");
      toast.success(`Email de test envoyé depuis ${status?.username || "votre boîte"} — vérifiez votre boîte !`);
      load();
    } catch (e) {
      toast.error(fmtError(e));
      load();
    } finally {
      setTestBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Revenir au service d'envoi par défaut ? L'adresse du domaine ne sera plus l'expéditeur.")) return;
    try {
      await api.delete("/admin/smtp");
      toast.success("Configuration email du site retirée");
      load();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500";
  const labelCls = "mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500";

  if (!status) return null;

  return (
    <section className="border border-white/[0.08] bg-surface p-8" data-testid="admin-email-section">
      <h2 className="flex items-center gap-3 font-display text-2xl font-medium">
        <Mail className="h-5 w-5 text-amber-500" /> Email du site
      </h2>

      {status.configured ? (
        <div className="mt-5 space-y-4">
          <p className="flex items-center gap-2 text-sm text-emerald-400" data-testid="smtp-configured">
            <CheckCircle2 className="h-4 w-4" />
            Les emails du site partent de {status.username} (via {status.host})
          </p>
          {status.last_error && (
            <p className="flex items-start gap-3 border border-red-500/40 bg-red-500/10 p-4 text-xs leading-relaxed text-red-300" data-testid="smtp-last-error">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Dernier envoi : la boîte {status.host} a refusé ({status.last_error})
                {status.last_error_at ? ` — le ${new Date(status.last_error_at).toLocaleString("fr-FR")}` : ""}.
                Le service d'envoi par défaut a pris le relais pour cet email. Renvoyez un test ci-dessous
                ou reconnectez la boîte si le problème persiste.
              </span>
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={sendTest} disabled={testBusy} data-testid="smtp-send-test"
              className="flex items-center gap-2 bg-amber-500 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
            >
              {testBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {testBusy ? "Envoi en cours…" : "Renvoyer un email de test"}
            </button>
            <button
              onClick={disconnect}
              data-testid="smtp-disconnect"
              className="flex items-center gap-2 border border-red-500/40 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-red-400 transition-colors hover:bg-red-500 hover:text-black"
            >
              <Unplug className="h-4 w-4" /> Revenir au service par défaut
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-3 text-sm leading-relaxed text-zinc-400">
            Par défaut, les emails partent d'une adresse technique de la plateforme. Connectez votre
            boîte <span className="text-zinc-200">contact@laconsoledesartistes.fr</span> (IONOS) pour que
            tout parte de votre domaine. Le mot de passe est celui de cette boîte (créé dans votre
            espace IONOS) — il reste stocké sur ce serveur, jamais affiché. Un email de test vous est
            envoyé pour valider avant activation.
          </p>
          <form onSubmit={connect} className="mt-5 space-y-4" data-testid="smtp-connect-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <label className={labelCls}>Serveur SMTP</label>
                <input required value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })}
                       data-testid="smtp-host" className={inputCls} placeholder="smtp.ionos.fr" />
              </div>
              <div>
                <label className={labelCls}>Port</label>
                <input required type="number" value={form.port} onChange={(e) => setForm({ ...form, port: parseInt(e.target.value) || 587 })}
                       data-testid="smtp-port" className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Adresse email (compte)</label>
              <input required type="email" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                     data-testid="smtp-username" className={inputCls} placeholder="contact@laconsoledesartistes.fr" />
            </div>
            <div>
              <label className={labelCls}>Mot de passe de la boîte</label>
              <div className="relative">
                <input required type={showPwd ? "text" : "password"} value={form.password}
                       onChange={(e) => setForm({ ...form, password: e.target.value })}
                       data-testid="smtp-password" className={inputCls}
                       placeholder="Mot de passe créé dans votre espace IONOS" />
                <button type="button" onClick={() => setShowPwd((s) => !s)} data-testid="smtp-toggle-visibility"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-400"
                        title="Afficher/masquer">
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit" disabled={busy} data-testid="smtp-connect-submit"
              className="flex items-center gap-2 bg-amber-500 px-6 py-3 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
              {busy ? "Test d'envoi en cours…" : "Activer et m'envoyer un email test"}
            </button>
          </form>
        </>
      )}
    </section>
  );
};
